import React from 'react';
import { Link } from 'react-router-dom';
import { getStorefrontVariantAvailability } from '../../utils/storefrontVariantUtils';

/**
 * Featured-products section block: a themed surface, centered section
 * header, and a 4-up responsive grid of product cards with badge +
 * wishlist heart. Used three times on HomePage (Best Sellers,
 * Trending, New Arrivals) — each invocation differs only in copy,
 * badge color, surface, and the product list it gets handed.
 *
 * Why bundled in one file: card, section header, and the row wrapper
 * are never used independently — pulling them apart would just spread
 * one block of code across four files. Constants and the two inline
 * SVGs are local on purpose to keep the section self-contained.
 *
 * The wishlist/format/path helpers stay as props rather than imported
 * here, because HomePage owns the dispatch/state for those — passing
 * them in keeps this component pure and easy to drop into another
 * page (e.g., a category landing or theme variant) without picking up
 * HomePage's redux wiring.
 */

const CARD_RADIUS = '12px';
const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.06)';
const CARD_HOVER_SHADOW = '0 8px 25px rgba(0,0,0,0.08)';
const SECTION_MAX_WIDTH = '1200px';
const SECTION_HPAD = '1.5rem';

const PackageIcon = ({ size = 48, color = '#d1d5db' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const HeartIcon = ({ filled = false, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? '#ec4899' : 'none'} stroke={filled ? '#ec4899' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const SectionHeader = ({ title, subtitle, themeStyles, theme }) => (
  <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
    <h2 style={{
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: '700',
      color: themeStyles?.text || '#111827',
      marginBottom: '0.5rem',
      fontFamily: theme?.fonts?.heading || 'inherit'
    }}>
      {title}
    </h2>
    <div style={{
      width: '40px',
      height: '3px',
      backgroundColor: themeStyles?.primary || '#2563eb',
      borderRadius: '2px',
      margin: '0 auto 0.75rem'
    }} />
    {subtitle && (
      <p style={{
        fontSize: '1rem',
        color: themeStyles?.textMuted || '#6b7280',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        {subtitle}
      </p>
    )}
  </div>
);

const FeaturedProductCard = ({
  product,
  badgeText,
  badgeColor,
  themeStyles,
  formatPrice,
  getStorePath,
  toggleWishlist,
  isInWishlist,
  wishlistLoading
}) => {
  const productId = product.id || product._id;
  const inWishlist = isInWishlist(productId);
  const hasImage = product.primaryImage || (product.gallery && product.gallery.length > 0);
  const imageUrl = product.primaryImage || (product.gallery && product.gallery[0]);
  const availability = getStorefrontVariantAvailability(product);
  const isOutOfStock = !availability.hasStock;

  return (
    <Link
      to={getStorePath(`product/${product.slug || product._id || product.id}`)}
      style={{ textDecoration: 'none', display: 'block' }}
    >
      <div
        style={{
          backgroundColor: themeStyles?.background || 'white',
          borderRadius: CARD_RADIUS,
          overflow: 'hidden',
          border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
          boxShadow: CARD_SHADOW,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = CARD_HOVER_SHADOW;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = CARD_SHADOW;
        }}
      >
        <div style={{
          width: '100%',
          aspectRatio: '1/1',
          backgroundColor: themeStyles?.backgroundAlt || '#f9fafb',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {hasImage ? (
            <img
              src={imageUrl}
              alt={product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PackageIcon size={48} color={themeStyles?.textMuted || '#d1d5db'} />
            </div>
          )}

          {badgeText && (
            <div style={{
              position: 'absolute',
              top: '0.75rem',
              left: '0.75rem',
              backgroundColor: badgeColor || themeStyles?.primary || '#2563eb',
              color: 'white',
              padding: '0.25rem 0.625rem',
              borderRadius: '6px',
              fontSize: '0.6875rem',
              fontWeight: '600',
              letterSpacing: '0.3px',
              zIndex: 5
            }}>
              {badgeText}
            </div>
          )}

          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(productId, e); }}
            disabled={wishlistLoading}
            aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            style={{
              position: 'absolute',
              top: '0.75rem',
              right: '0.75rem',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              // Use the surface neutral so the button flips with
              // dark mode instead of staying a pure-white circle.
              // Heavier shadow makes up for the lost semi-transparency.
              backgroundColor: themeStyles?.background || '#ffffff',
              border: 'none',
              cursor: wishlistLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              zIndex: 5,
              transition: 'transform 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <HeartIcon filled={inWishlist} size={16} />
          </button>

          {isOutOfStock && (
            <div style={{
              position: 'absolute',
              inset: 0,
              // Dark scrim works on any photo + flips correctly in
              // dark mode (a half-white overlay washed the card out).
              backgroundColor: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '0.375rem 0.75rem',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>Out of Stock</span>
            </div>
          )}
          {product.activeSale && !isOutOfStock && (
            <div style={{
              position: 'absolute',
              top: '0.625rem',
              left: '0.625rem',
              backgroundColor: '#111827',
              color: 'white',
              padding: '0.25rem 0.55rem',
              borderRadius: '6px',
              fontSize: '0.6875rem',
              fontWeight: '700',
              letterSpacing: '0.03em'
            }}>
              {product.activeSale.badgeText || 'SALE'}
            </div>
          )}
        </div>

        <div style={{ padding: '0.875rem' }}>
          <h3 style={{
            fontSize: '0.9375rem',
            fontWeight: '500',
            color: themeStyles?.text || '#111827',
            marginBottom: '0.5rem',
            lineHeight: '1.4',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.625rem'
          }}>
            {product.name}
          </h3>
          <div>
            <span style={{
              fontSize: '1.0625rem',
              fontWeight: '700',
              color: themeStyles?.primary || '#2563eb'
            }}>
              {formatPrice(product.price)}
            </span>
            {product.compareAtPrice && Number(product.compareAtPrice) > Number(product.price) && (
              <span style={{
                marginLeft: '0.45rem',
                fontSize: '0.8125rem',
                color: themeStyles?.textMuted || '#6b7280',
                textDecoration: 'line-through'
              }}>
                {formatPrice(product.compareAtPrice)}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

/**
 * One featured-products row.
 *
 * Renders nothing when the products list is empty — so callers don't
 * need to wrap in conditionals. The `max` prop caps how many cards
 * actually render (the parent's slice limit, e.g. BEST_SELLERS_MAX).
 *
 * `surface` picks between the two alternating section backgrounds:
 *   - 'base'  → themeStyles.background    (e.g., Trending)
 *   - 'alt'   → themeStyles.backgroundAlt (e.g., Best Sellers, New Arrivals)
 * This keeps callers from having to know the exact token names and
 * makes the alternating-band pattern explicit.
 */
const FeaturedProductsRow = ({
  products,
  max,
  title,
  subtitle,
  badgeText,
  badgeColor,
  surface = 'base',
  themeStyles,
  theme,
  formatPrice,
  getStorePath,
  toggleWishlist,
  isInWishlist,
  wishlistLoading
}) => {
  if (!products || products.length === 0) return null;

  const backgroundColor = surface === 'alt'
    ? (themeStyles?.backgroundAlt || '#fafafa')
    : (themeStyles?.background || 'white');

  const visible = typeof max === 'number' ? products.slice(0, max) : products;

  return (
    <div style={{ backgroundColor, padding: '4rem 0' }}>
      <div style={{ maxWidth: SECTION_MAX_WIDTH, margin: '0 auto', padding: `0 ${SECTION_HPAD}` }}>
        <SectionHeader
          title={title}
          subtitle={subtitle}
          themeStyles={themeStyles}
          theme={theme}
        />

        <div className="hp-featured-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1.25rem'
        }}>
          {visible.map((product) => (
            <FeaturedProductCard
              key={product._id || product.id}
              product={product}
              badgeText={badgeText}
              badgeColor={badgeColor}
              themeStyles={themeStyles}
              formatPrice={formatPrice}
              getStorePath={getStorePath}
              toggleWishlist={toggleWishlist}
              isInWishlist={isInWishlist}
              wishlistLoading={wishlistLoading}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedProductsRow;
